import Portrait from '@/icons/portriat';
import SavingsCalculatorBlobContainer from './savings-calculator-blob-container';
import Spacer from '@/common/components/spacer';
import { Form } from 'react-router';
import { Switch } from '@/shadcn-ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger } from '@/shadcn-ui/select';
import {
  CreditRange,
  ownershipYearsOptions,
} from '@/catalog/constants/savings-calculator';
import { CreditScore } from '@/catalog/types';
import MileageDescription from './mileage-description';
import { AgeRange } from '@/catalog/types/age-range';
import EditPencil from '@/icons/edit-pencil';
import { pluralize } from '@/common/utils/pluralize';
import SavingsCalculatorCollapsibleSection from './savings-calculator-collapsible-section';
import { MileageOption } from '@/catalog/utils/get-miles-per-year-options';

const ownershipYearsDisplay = (years: number) => {
  return (
    ownershipYearsOptions.find((option) => parseInt(option.value) === years)?.name || ''
  );
};

const creditScoreName = (creditScoreId: string, creditScores: CreditScore[]) => {
  const creditScore = creditScores.find(
    (creditScore) => creditScore.id === creditScoreId
  );
  return `${creditScore?.range} (${creditScore?.name})`;
};

const creditScoreRange = (creditScoreId: string, creditScores: CreditScore[]) => {
  const creditScore = creditScores.find(
    (creditScore) => creditScore.id === creditScoreId
  );
  return `${creditScore?.range}`;
};

const abbreviatedAnnualMileage = (mileage: number) => {
  switch (mileage) {
    case 500:
    case 850:
      return '10k';
    case 1200:
      return '15k';
    case 2000:
      return '24k';
    case 0:
    default:
      return '5k';
  }
};

interface AboutMeTextPillProps {
  label: string;
  value?: string;
}

const AboutMeTextPill = ({ label, value }: AboutMeTextPillProps) => {
  return (
    <p className="body2 flex-shrink-0 rounded-[18px] border border-flexcar-neutral80 bg-flexcar-neutral100 px-[8px] py-[4px] font-sans text-flexcar-neutral1">
      {label}: <span className="body2-strong">{value}</span>
    </p>
  );
};

interface SavingsInputFormProps {
  atOrOver25: boolean;
  handleAgeChangeDelegate: (checked: boolean) => void;
  disableAge: boolean;
  creditScore: CreditRange;
  ageRanges?: AgeRange[];
  ageRangeId?: string;
  handleCreditScoreChangeDelegate: (event: CreditRange) => void;
  disableCreditScore: boolean;
  creditScores: CreditScore[];
  ownershipYears: number;
  handleOwnershipYearsChangeDelegate: (event: string) => void;
  planMileage: number;
  handlePlanMileageChangeDelegate: (event: string) => void;
  handleAgeRangeChangeDelegate: (id: string) => void;
  milesPerYearOptions: MileageOption[];
}
export default function SavingsInputForm({
  atOrOver25,
  handleAgeChangeDelegate,
  handleAgeRangeChangeDelegate,
  disableAge,
  creditScore,
  creditScores,
  ageRangeId,
  ageRanges,
  handleCreditScoreChangeDelegate,
  disableCreditScore,
  ownershipYears,
  handleOwnershipYearsChangeDelegate,
  planMileage,
  handlePlanMileageChangeDelegate,
  milesPerYearOptions,
}: SavingsInputFormProps) {
  const subTrigger = (
    <div className="flex flex-wrap gap-[4px]">
      <AboutMeTextPill
        label="Age"
        value={
          !ageRanges
            ? atOrOver25
              ? '25+'
              : '< 25'
            : ageRanges.find((ar) => ar.id === ageRangeId)?.display_name
        }
      />
      <AboutMeTextPill
        label="Credit score"
        value={creditScoreRange(creditScore, creditScores)}
      />
      <AboutMeTextPill
        label="Typically own"
        value={`${ownershipYears}${ownershipYears > 5 ? '+' : ''} ${pluralize(ownershipYears, 'yr')}`}
      />
      <AboutMeTextPill
        label="Annual mileage"
        value={abbreviatedAnnualMileage(planMileage)}
      />
    </div>
  );

  return (
    <SavingsCalculatorBlobContainer className="p-[16px]">
      <SavingsCalculatorCollapsibleSection
        title="Let's make it all about you"
        titleIcon={<Portrait className="h-[20px] w-[20px]" />}
        subTrigger={subTrigger}
        iconOverride={{
          closedIcon: <EditPencil className="mr-[4px] flex-shrink-0" />,
        }}
      >
        <Form>
          <div>
            <div className="flex flex-row justify-between gap-2">
              <div className="min-w-[117px] flex-grow basis-[calc(33.333%-0.5rem)]">
                <p className="body2 text-flexcar-neutral1">Age</p>
                <Spacer variant="height4" />

                {!ageRanges ? (
                  <div className="flex w-fit flex-row gap-[8px] rounded-[16px] border-[1.5px] border-solid border-flexcar-neutral80 bg-flexcar-neutral100 px-[16px] py-[12px] shadow-inputShadow">
                    <p className="body">25+</p>
                    <Switch
                      checked={atOrOver25}
                      onCheckedChange={handleAgeChangeDelegate}
                      className="data-[state=checked]:bg-[linear-gradient(270deg,#3A00E5_35.42%,#8901E9_100%)]"
                      disabled={disableAge}
                    />
                  </div>
                ) : null}
                {ageRanges ? (
                  <Select
                    defaultValue={ageRangeId}
                    onValueChange={handleAgeRangeChangeDelegate}
                    disabled={disableAge}
                  >
                    <SelectTrigger className="rounded-[16px] border-[1.5px] border-solid border-flexcar-neutral80 bg-flexcar-neutral100 px-[16px] py-[24px] text-[16px] shadow-inputShadow">
                      {ageRanges.find((ar) => ar.id === ageRangeId)?.display_name}
                    </SelectTrigger>

                    <SelectContent>
                      {ageRanges.map((ar) => (
                        <SelectItem key={ar.id} value={ar.id}>
                          {ageRanges.find((a) => a.id === ar.id)?.display_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : null}
              </div>
              <div className="min-w-[180px] flex-grow basis-[calc(66.666%-0.5rem)]">
                <p className="body2 text-flexcar-neutral1">Credit score</p>
                <Spacer variant="height4" />
                <Select
                  defaultValue={creditScore}
                  onValueChange={handleCreditScoreChangeDelegate}
                  disabled={disableCreditScore}
                >
                  <SelectTrigger className="rounded-[16px] border-[1.5px] border-solid border-flexcar-neutral80 bg-flexcar-neutral100 px-[16px] py-[24px] text-[16px] shadow-inputShadow">
                    <p className="min-w-[135px] overflow-hidden text-ellipsis whitespace-nowrap text-start">
                      {creditScoreName(creditScore, creditScores)}
                    </p>
                  </SelectTrigger>
                  {/* need z-index to be at least higher than 51 so that it goes above the popover from the slider */}
                  <SelectContent>
                    {creditScores.map((cs) => (
                      <SelectItem key={cs.id} value={cs.id}>
                        {creditScoreName(cs.id, creditScores)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Spacer variant="height16" />

            <p className="body2 text-flexcar-neutral10">
              If you were to buy this car, how long would you want to keep it?
            </p>
            <Spacer variant="height4" />
            <Select
              defaultValue={`${ownershipYears}`}
              onValueChange={handleOwnershipYearsChangeDelegate}
            >
              <SelectTrigger className="rounded-[16px] border-[1.5px] border-solid border-flexcar-neutral80 bg-flexcar-neutral100 px-[16px] py-[24px] text-[16px] shadow-inputShadow">
                {ownershipYearsDisplay(ownershipYears)}
              </SelectTrigger>
              <SelectContent>
                {ownershipYearsOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Spacer variant="height16" />

            <p className="body2 text-flexcar-neutral10">Typical annual mileage</p>
            <Spacer variant="height4" />
            <Select
              value={`${planMileage}`}
              onValueChange={handlePlanMileageChangeDelegate}
            >
              <SelectTrigger className="rounded-[16px] border-[1.5px] border-solid border-flexcar-neutral80 bg-flexcar-neutral100 px-[16px] py-[24px] text-[16px] shadow-inputShadow">
                <MileageDescription planMileage={planMileage} />
              </SelectTrigger>
              <SelectContent className="max-w-[calc(100vw-50px)]">
                {milesPerYearOptions?.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <MileageDescription planMileage={parseInt(option.value)} />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Form>
      </SavingsCalculatorCollapsibleSection>
    </SavingsCalculatorBlobContainer>
  );
}
